import { create } from "zustand";

import { getJSON } from "../lib/api";
import { adaptProProfile } from "../lib/adapt";
import { IS_PREVIEW } from "../lib/pro-mode";
import { FIXTURE_PRO_PROFILE } from "../lib/fixtures";
import type { GetProToken } from "../hooks/useProToken";
import type { ProProfile } from "../lib/types";
import type { ProProfileVM } from "../lib/view-model";

// SILL-346 (follow-up item 3): one shared `/api/professional/profile` fetch.
//
// Modeled on the consumer's readiness store (ADR-027). The professional profile
// (org/role + founding) is read by FOUR surfaces — the ProAuthGate access probe,
// HeaderNav, Settings ("Your account"), and the Profile page. Holding it in a
// per-hook reducer made each surface refetch on mount; a module-level store
// dedupes them into a single network call and keeps the data warm across route
// navigation.
//
// Store responsibilities (mirroring readiness):
//   * `ensureLoaded(getToken)` — fetch on first request, no-op afterwards.
//     Concurrent callers share one in-flight promise → exactly one fetch.
//   * `refresh(getToken)`      — explicit revalidation (e.g. after a Profile
//     edit). Existing data stays present during the refetch.
//   * `reset()`                — clears state for sign-out / test isolation.
//
// Anti-flicker: `isFetching` + `hasLoaded` are the raw signals; the consuming
// hook derives `isLoading` (first paint) vs `isRefreshing` (background). `data`
// is preserved on error so a transient failure never blanks a populated card.
type ProfileStore = {
  data: ProProfileVM | null;
  error: Error | null;
  isFetching: boolean;
  hasLoaded: boolean;

  ensureLoaded: (getToken: GetProToken) => Promise<void>;
  refresh: (getToken: GetProToken) => Promise<void>;
  reset: () => void;
};

// Module-scoped in-flight reference (outside store state so promise tracking
// doesn't trigger subscriber re-renders).
let inFlight: Promise<void> | null = null;

/** Resolve the profile view model — fixture in preview, live fetch otherwise. */
async function fetchProfile(getToken: GetProToken): Promise<ProProfileVM> {
  if (IS_PREVIEW) {
    return FIXTURE_PRO_PROFILE;
  }
  const token = (await getToken()) ?? undefined;
  const res = await getJSON<ProProfile>("/api/professional/profile", token);
  return adaptProProfile(res);
}

async function runFetch(
  getToken: GetProToken,
  set: (partial: Partial<ProfileStore>) => void,
): Promise<void> {
  set({ isFetching: true });
  try {
    const data = await fetchProfile(getToken);
    set({ data, error: null, hasLoaded: true, isFetching: false });
  } catch (e) {
    // Keep `data` + `hasLoaded` so a transient error doesn't blank a populated
    // card (the auth gate, once allowed, must also stay allowed).
    set({ error: e as Error, isFetching: false });
  }
}

export const useProfileStore = create<ProfileStore>((set, get) => ({
  data: null,
  error: null,
  isFetching: false,
  hasLoaded: false,

  ensureLoaded: async (getToken) => {
    if (get().hasLoaded) return;
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  refresh: async (getToken) => {
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  reset: () => {
    inFlight = null;
    set({ data: null, error: null, hasLoaded: false, isFetching: false });
  },
}));
